import React, { useEffect, Suspense, lazy } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GoogleOAuthProvider } from '@react-oauth/google';
import Config from './Config';
import { Remove_User, Set_Cart_Modal_Open } from "./Redux/actions"
import ProfilePage from "./Pages/Profile";
import Help from "./Pages/Help";
import Categories from "./Pages/Categories";
import Home from "./Pages/Home";
import MyOrders from "./Pages/MyOrders";
import OrderDetails from "./Pages/OrderDetails";
import SellerDashboard from "./Pages/Seller/SellerDashboard";
import ProductPage from "./Pages/ProductPage";
import AllProducts from "./Pages/AllProducts";
import BottomNavigation from "./components/BottomNavigation";
import ScrollToTop from "./components/ScrollToTop";
import ReturnsRefunds from "./Pages/ReturnsRefunds";
import PaymentReturn from "./Pages/PaymentReturn";
import NotFoundPage from "./components/NotfoundPage";
// import Cart from "./components/Cart";

const Cart = lazy(() => import("./components/Cart")); // load cart component only when the user opens the cart, not on initial load

function App() {
  const user = useSelector((state) => state.UserReducer.user);
  const isCartModalOpen = useSelector((state) => state.CartReducer.isCartModalOpen);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isSessionExpired = () => {
    if (user) {
      const expireAt = user?.expireAt;
      const currentTime = moment().valueOf();
      const isExpired = moment(currentTime).isAfter(expireAt);
      if (isExpired) {
        dispatch(Remove_User());
        navigate("/");
      }
    }
  };

  useEffect(() => isSessionExpired(), []);

  return (
    <GoogleOAuthProvider clientId={Config.googleClientId}>
      <ScrollToTop />
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={false}
        theme="colored"
        closeButton={false}
        limit={1}
        transition={Slide}
        toastClassName={({ type }) =>
          `${type === "success" ? "bg-kuality-blue" :
            type === "error" ? "bg-red-600" :
              type === "warning" ? "bg-yellow-600" :
                type === "info" ? "bg-kuality-blue" : "bg-kuality-blue"
          } text-white rounded-full px-4 py-3 max-w-[90vw] mx-auto flex items-center justify-center shadow-lg text-sm font-medium tracking-wide my-4`
        }
      />

      {/*suspense wrapper handles the loading state while Cart is being fetched*/}
      {isCartModalOpen && (
        <Suspense fallback={null}> { /* null fallback to avoid flicker during loading */}
          <Cart
            isOpen={isCartModalOpen}
            onClose={() => dispatch(Set_Cart_Modal_Open(false))}
          />
        </Suspense>
      )}

      {/* Routes for Logged-Out Users */}
      {!user && (
        <>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/products/:type" element={<AllProducts />} />
            <Route path="/orders" element={<MyOrders />} />
            <Route path="/order-details" element={<OrderDetails />} />
            <Route path="/payment/status" element={<PaymentReturn />} />
            <Route path="/help" element={<Help />} />
            <Route path="/returns&refunds" element={<ReturnsRefunds />} />
            <Route path="*" element={<NotFoundPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Routes>
          <BottomNavigation />
        </>
      )}

      {/* Routes for Logged-In Users */}
      {user && (
        <>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/products/:type" element={<AllProducts />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/orders" element={<MyOrders />} />
            <Route path="/order-details" element={<OrderDetails />} />
            <Route path="/help" element={<Help />} />
            <Route path="/returns&refunds" element={<ReturnsRefunds />} />
            <Route path="/payment/status" element={<PaymentReturn />} />
            <Route path="/admin" element={<SellerDashboard />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <BottomNavigation />
        </>
      )}

    </GoogleOAuthProvider>
  );
}

export default App;